import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Bell, Mail, AlertTriangle, FileText, CheckCircle, User, Clock, Settings } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function NotificationSettings({ open, onOpenChange }) {
  const [settings, setSettings] = useState({
    // In-app notifications
    employee_submissions: true,
    insufficiencies: true,
    edit_requests: true,
    case_updates: true,
    reports_ready: true,
    // Email preferences
    email_critical_only: true,
    email_digest: false,
    email_insufficiency_alerts: true,
    email_final_reports: true,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchSettings();
    }
  }, [open]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/notification-settings');
      setSettings(prev => ({
        ...prev,
        ...response.data
      }));
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put('/notification-settings', {
        employee_submissions: settings.employee_submissions,
        insufficiencies: settings.insufficiencies,
        edit_requests: settings.edit_requests,
        case_updates: settings.case_updates,
        reports_ready: settings.reports_ready,
        email_critical_only: settings.email_critical_only,
        email_digest: settings.email_digest,
        email_insufficiency_alerts: settings.email_insufficiency_alerts,
        email_final_reports: settings.email_final_reports,
      });
      toast.success('Notification settings saved');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-600" />
            Notification Settings
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">Loading settings...</div>
        ) : (
          <div className="space-y-6 py-4">
            {/* In-App Notifications Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Bell className="w-4 h-4 text-blue-600" />
                <h3 className="font-semibold text-slate-800">In-App Notifications</h3>
              </div>
              <div className="space-y-3 pl-6">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-emerald-500" />
                    Employee Submissions
                  </Label>
                  <Switch
                    checked={settings.employee_submissions}
                    onCheckedChange={(v) => updateSetting('employee_submissions', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    Insufficiencies
                  </Label>
                  <Switch
                    checked={settings.insufficiencies}
                    onCheckedChange={(v) => updateSetting('insufficiencies', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Edit Requests
                  </Label>
                  <Switch
                    checked={settings.edit_requests}
                    onCheckedChange={(v) => updateSetting('edit_requests', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-purple-500" />
                    Case Updates
                  </Label>
                  <Switch
                    checked={settings.case_updates}
                    onCheckedChange={(v) => updateSetting('case_updates', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Reports Ready
                  </Label>
                  <Switch
                    checked={settings.reports_ready}
                    onCheckedChange={(v) => updateSetting('reports_ready', v)}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Email Preferences Section */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-4 h-4 text-rose-600" />
                <h3 className="font-semibold text-slate-800">Email Preferences</h3>
                <Badge variant="outline" className="text-xs text-slate-500">Minimize emails</Badge>
              </div>
              <div className="space-y-3 pl-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Critical Alerts Only</Label>
                    <p className="text-xs text-slate-500">Only email for urgent items</p>
                  </div>
                  <Switch
                    checked={settings.email_critical_only}
                    onCheckedChange={(v) => updateSetting('email_critical_only', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Insufficiency Alerts</Label>
                    <p className="text-xs text-slate-500">Email when insufficiency is raised</p>
                  </div>
                  <Switch
                    checked={settings.email_insufficiency_alerts}
                    onCheckedChange={(v) => updateSetting('email_insufficiency_alerts', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Final Reports</Label>
                    <p className="text-xs text-slate-500">Email when reports are ready</p>
                  </div>
                  <Switch
                    checked={settings.email_final_reports}
                    onCheckedChange={(v) => updateSetting('email_final_reports', v)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Daily Digest</Label>
                    <p className="text-xs text-slate-500">Combine into one daily email</p>
                  </div>
                  <Switch
                    checked={settings.email_digest}
                    onCheckedChange={(v) => updateSetting('email_digest', v)}
                  />
                </div>
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Smart Notifications:</strong> Notifications are automatically filtered based on your role. 
                You'll only see relevant updates for your responsibilities.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
